import styled, { css } from 'styled-components';
import { colors, r4, spacing } from '@ardoq/design-tokens';
import { text1 } from '@ardoq/typography';
import { DECORATOR_SELECTOR } from '@ardoq/decorators';
import { focusableCSS } from '@ardoq/style-helpers';
import { TextOverflow } from '@ardoq/popovers';
import { Icon, ICON_SELECTOR, IconName } from '@ardoq/icons';
import { AriaAttributes } from 'react';

const MenuItemLabel = styled(TextOverflow)`
  line-height: normal;
  width: 100%;
`;

const MenuItem = styled.button<{
  $isSelected?: boolean;
}>`
  border: none;
  ${focusableCSS}
  ${text1};
  height: 40px;
  text-align: start;
  padding: ${spacing.s8};
  cursor: pointer;
  border-radius: ${r4};
  background: none;
  color: ${colors.textDefault};
  &:hover {
    background-color: ${colors.surfaceHover};
  }
  ${({ $isSelected }) =>
    $isSelected &&
    css`
      color: ${colors.textDefault};
      background-color: ${colors.surfaceSelected};
      &:hover {
        background-color: ${colors.surfaceSelected};
      }
    `}

  /* Small exception here
    maybe later we will introduce
    sizes for decorators,
    but for this only case it makes
    things too complex */
  ${DECORATOR_SELECTOR} {
    min-height: auto;
  }

  ${ICON_SELECTOR} {
    color: ${colors.icon};
  }

  display: flex;
  align-items: center;
  gap: ${spacing.s8};
  ${({ disabled }) =>
    disabled &&
    css`
      color: ${colors.textDisabled};
      ${ICON_SELECTOR} {
        color: ${colors.iconActionDisabled};
      }
      &:hover,
      &:focus {
        background-color: transparent;
        cursor: default;
      }
    `}
`;

export const SIDEBAR_MENU_MENU_ITEM_SELECTOR = MenuItem.toString();

export type SidebarMenuItemProps = {
  label: React.ReactNode;
  isDisabled?: boolean;
  onClick?: () => void;
  dataClickId?: string;
  dataTestId?: string;
  leftContent?: React.ReactNode;
  rightContent?: React.ReactNode;
  isSelected?: boolean;
  iconName?: IconName;
  ariaCurrent?: AriaAttributes['aria-current'];
  className?: string;
};
export const SidebarMenuItem = ({
  label,
  leftContent,
  rightContent,
  dataClickId,
  dataTestId,
  isDisabled,
  isSelected,
  onClick,
  iconName,
  ariaCurrent,
  className,
}: SidebarMenuItemProps) => {
  return (
    <MenuItem
      onClick={onClick}
      disabled={isDisabled}
      data-click-id={dataClickId}
      data-test-id={dataTestId}
      $isSelected={isSelected}
      aria-current={ariaCurrent}
      className={className}
    >
      {leftContent} {iconName && <Icon iconName={iconName} />}
      <MenuItemLabel>{label}</MenuItemLabel>
      {rightContent}
    </MenuItem>
  );
};
